/**
 * Consent scope (ML-D6). Prior opt-in is required in the EEA, the United Kingdom and
 * Switzerland (ePrivacy Directive Art. 5(3) as implemented, read with GDPR Art. 4(11) and 7,
 * and the UK PECR). Region is inferred from the browser's IANA time zone only — no IP
 * lookup, nothing leaves the page to decide this.
 *
 * The rule asks for consent when the time zone is
 *   1. any `Europe/*` zone — every European time zone, so the privacy disclosure's sentence
 *      "Where the device is set to a European time zone, you are asked before any measurement
 *      tag loads" stays true (this keeps the non-EEA European zones production already asked);
 *   2. a European zone that IANA files outside `Europe/`;
 *   3. a zone of EEA, UK or Swiss territory outside geographic Europe, and Greenland;
 *   4. a legacy IANA alias of one of the above;
 * or when the time zone cannot be read at all (reading it throws).
 */

/** Zones in Europe that IANA does not file under `Europe/`. */
export const EUROPEAN_ZONES_OUTSIDE_EUROPE_AREA: readonly string[] = [
  'Atlantic/Azores', // PT
  'Atlantic/Madeira', // PT
  'Atlantic/Canary', // ES
  'Atlantic/Reykjavik', // IS
  'Atlantic/Faroe', // FO (Kingdom of Denmark; geographically Europe)
  'Atlantic/Faeroe', // FO — the spelling Chromium/ICU reports
  'Atlantic/Jan_Mayen', // SJ (Norway)
  'Arctic/Longyearbyen', // SJ (Norway)
  'Asia/Nicosia', // CY
  'Asia/Famagusta', // CY
];

/**
 * EEA territory outside geographic Europe (EU outermost regions, Ceuta and Melilla), plus
 * Greenland.
 *
 * ☧ 0, 2026-09-12: "just get rid of greenland." Greenland left the EU in 1985 and is not in the
 * EEA, so an earlier reading excluded it and flagged the call for a ruling. The ruling is to stop
 * treating it as a question: it is Danish territory, its visitors are asked like everyone else,
 * and the cost of asking someone who did not need to be asked is a banner.
 */
export const EEA_ZONES_OUTSIDE_EUROPE: readonly string[] = [
  'Africa/Ceuta', // ES — Ceuta and Melilla
  'America/Cayenne', // GF — French Guiana
  'America/Guadeloupe', // GP
  'America/Martinique', // MQ
  'America/Marigot', // MF — Saint-Martin
  'America/Nuuk', // GL — Greenland (Kingdom of Denmark)
  'America/Godthab', // GL — the legacy IANA name for the same zone
  'America/Danmarkshavn', // GL
  'America/Scoresbysund', // GL
  'America/Thule', // GL
  'Indian/Reunion', // RE
  'Indian/Mayotte', // YT
];

/** Legacy IANA names that link to an EEA, UK or Swiss zone. */
export const LEGACY_ALIASES: readonly string[] = [
  'CET',
  'EET',
  'Eire',
  'GB',
  'GB-Eire',
  'Iceland',
  'MET',
  'Poland',
  'Portugal',
  'WET',
];

const LISTED = new Set<string>([...EUROPEAN_ZONES_OUTSIDE_EUROPE_AREA, ...EEA_ZONES_OUTSIDE_EUROPE, ...LEGACY_ALIASES]);

export function isConsentTimeZone(timeZone: string): boolean {
  return timeZone.startsWith('Europe/') || LISTED.has(timeZone);
}

export function browserTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
}

/**
 * True when this visitor must be asked before any measurement tag loads.
 * A reader that throws means the zone is undetectable → ask. An empty zone (no throw) is
 * treated as not required, exactly as production did.
 */
export function consentRequired(readTimeZone: () => string = browserTimeZone): boolean {
  try {
    return isConsentTimeZone(readTimeZone());
  } catch {
    return true;
  }
}
